import React, { PropsWithChildren, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Spin, Button, Steps, StepProps, Modal } from 'ALIASED-antd';
import { breakpoints } from '@lib/breakpoints';
import { useSessionAppointmentId } from '@hooks/useSessionAppointmentId';
import { useSessionFormState } from '@hooks/useSessionFormState';
import Clinical from '@components/Clinicians/Session/Clinical';
import DataProtection from '@components/Clinicians/Session/DataProtection';
import { useTranslation } from 'react-i18next';
import { Actions } from '@components/Clinicians/Session/v2/Actions';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import useAppointmentGrabbed from '@hooks/useAppointmentGrabbed';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import useCallStatusUpdaters from '@hooks/useCallStatusUpdaters';
import useMyStatusUpdater from '@hooks/useMyStatusUpdater';
import { useEligibilityChecks } from '@hooks/useEligibilityChecks';
import { useSessionOutcomeMutation } from '@hooks/useSessionOutcomeMutation';
import SessionLoader from '@components/Clinicians/SessionLoader';
import useAppointmentMeta from '@components/Clinicians/Session/hooks/useAppointmentMeta';
import ClinicianMediaPermissionsWarning from '@components/MediaPermissions/ClinicianMediaPermissionsWarning';
import PatientStatusPanel from './PatientStatusPanel';
import Background from './v2/Background';
import EndAppointmentDropdown from './v2/EndAppointment';
import PatientInformation from '../PatientInformation';
import LetterGeneration from './LetterGeneration';
import AppointmentAlert from './v2/Components/AppointmentAlert';
import useIsSessionSaving from './hooks/useIsSessionSaving';
import { HeidiSessionProvider } from '@components/Clinicians/Session/hooks/useHeidiSession';
import { HeidiErrorProvider } from '@hooks/useHeidiError';
import useAppointmentDetails from '@hooks/appointments/useAppointmentDetails';

export const StepTabs = {
  BACKGROUND: 'BACKGROUND',
  SECURITY: 'SECURITY',
  CLINICAL: 'CLINICAL',
  ACTIONS: 'ACTIONS',
  OUTCOME: 'OUTCOME',
  LETTER: 'LETTER',
  END: 'END'
} as const;
export type ActiveTabType = keyof typeof StepTabs;

interface ActiveTabProps {
  title: React.ReactNode;
  id: ActiveTabType;
  stepComponent: React.FC;
}

const StyledFlow = styled.div`
  .step-container {
    display: flex;
    align-items: center;
    @media (min-width: 768px) and (max-width: 1045px) {
      margin: 20px 0px;
    }

    .ant-steps-item-disabled {
      cursor: default;
    }

    margin: 20px 16px;

    .ant-steps-item {
      max-width: 100%;
    }

    .ant-steps-item-container {
      .width: 100%;
      .ant-steps-item-title {
        width: 100%;
      }

      .flow-button {
        display: inline-block;
        width: 100%;
      }
    }

    .ant-steps-item:not(.ant-steps-item-active) > .ant-steps-item-container[role='button']:hover .ant-steps-item-title {
      color: var(--ant-color-text);
    }
    .ant-steps-item:not(.ant-steps-item-disabled) .ant-steps-item-container:focus-visible {
      outline: 2.5px solid #3b99fc;
      border-radius: 6px;
    }

    .ant-steps-item-disabled .ant-steps-item-container:has(button.endItem:focus-visible) {
      outline: 2.5px solid #3b99fc;
      border-radius: 6px;
    }

    button.endItem:focus-visible {
      outline: none !important;
      box-shadow: none !important;
    }

    .ant-steps {
      gap-x: 50px;
      justify-content: center;
      align-items: center;

      @media (min-width: 768px) and (max-width: 1045px) {
        justify-content: space-between;
        .ant-steps-item-title {
          max-width: fit-content;
          .flow-button {
            max-width: fit-content;
            font-size: 12px;
          }
          button {
            max-width: fit-content;
            span {
              font-size: 12px;
            }
          }
        }
      }
    }

    .ant-steps-item {
      margin-left: 0.5rem;
      margin-right: 0rem;
      padding-left: 0.5rem;
      @media (min-width: 768px) and (max-width: 1045px) {
        padding-left: 1.3rem;
        padding-right: 0.2rem;
      }
    }

    .ant-steps-item::before {
      left: 0.2rem;
      transition: none;
      @media (min-width: 768px) and (max-width: 1045px) {
      }
    }

    .ant-steps-item::after {
      width: 12px;
      height: 12px;
      top: 50%;
    }

    .ant-steps-item-content {
      margin: 0;
      @media (min-width: 768px) and (max-width: 1045px) {
        max-width: fit-content;
      }
    }

    .ant-steps-item-icon {
      display: none;
      height: 0;
    }

    .mobile-only {
      display: block;

      @media (min-width: 1024px) {
        display: none;
      }
    }

    .desktop-only {
      display: none;

      @media (min-width: 1024px) {
        display: block;
      }
    }
  }
`;

const ClinicianSessionStyled = styled.div`
  display: flex;
  width: 100%;

  .session-view {
    @media (max-width: ${breakpoints.md}px) {
      padding: 0 0 20px 0;
    }
    background-color: #f6f6f6;
    max-width: calc(100vw - 100px);
    padding: 20px;
    flex: 1;
    min-height: calc(100vh - 10rem);
    box-shadow: -2px 0 1px 0 rgba(0, 0, 0, 0.15);

    &__completed {
      max-width: unset;
    }
  }

  .call-window-expanded {
    max-width: 70vw;
  }

  .session-steps-content {
    background-color: white;
    box-shadow:
      rgba(50, 50, 93, 0.25) 0 2px 5px -1px,
      rgba(0, 0, 0, 0.3) 0 1px 3px -1px;
    min-height: 80vh;
    border-radius: 5px;
  }

  @media (max-width: ${breakpoints.md}px) {
    flex-direction: column;
  }
`;

const DEFAULT_VIRTUAL_ONLY_TABS = [StepTabs.OUTCOME, StepTabs.CLINICAL, StepTabs.ACTIONS];
const useVirtualOnlyWarning = ({
  isSessionLoading,
  activeTab
}: {
  isSessionLoading: boolean;
  activeTab: ActiveTabType;
}) => {
  const { appointment } = useSessionAppointment();
  const { t } = useTranslation('common');
  const { data: eligibilityChecks } = useEligibilityChecks();
  const isVirtualServicesOnly = !!eligibilityChecks?.VIRTUAL_ONLY;

  const [virtualOnlyWarningTabs, setVirtualOnlyWarningTabs] = useState<ActiveTabType[]>(
    () => DEFAULT_VIRTUAL_ONLY_TABS
  );

  // Display "virtual only" eligibility warning on the first visit to each of the OUTCOME, CLINICAL and ACTIONS tabs
  const showEligibilityWarningModal = useCallback(() => {
    if (
      appointment.appointment_completed ||
      isSessionLoading ||
      !isVirtualServicesOnly ||
      !virtualOnlyWarningTabs.includes(activeTab)
    ) {
      return null;
    }

    Modal.destroyAll();
    return Modal.warning({
      title: t('Warning'),
      modalRender: (originalNode) => <div data-testid="warning-modal">{originalNode}</div>,
      centered: true,
      // don't display the warning for this tab again
      onOk: () => setVirtualOnlyWarningTabs((prevState) => prevState.filter((tab) => tab !== activeTab)),
      content: (
        <>
          <div dangerouslySetInnerHTML={{ __html: t('eligibilityWarningModalHeader') }} />
          <div dangerouslySetInnerHTML={{ __html: t('eligibilityWarningModalContent') }} />
        </>
      ),
      okButtonProps: { type: 'primary' }
    });
  }, [
    activeTab,
    appointment.appointment_completed,
    isSessionLoading,
    isVirtualServicesOnly,
    t,
    virtualOnlyWarningTabs
  ]);

  // if the "virtual services only" eligibility toggles (perhaps because of vl-admin changes/React Query cache reload),
  // whenever the flag is seen to toggle *on* - reset the tabs that need the warning message displayed. See VLM-803.
  useEffect(() => {
    if (isVirtualServicesOnly) setVirtualOnlyWarningTabs(DEFAULT_VIRTUAL_ONLY_TABS);
  }, [isVirtualServicesOnly]);

  useEffect(() => {
    const renderModal = setTimeout(() => {
      showEligibilityWarningModal();
    }, 200);
    return () => {
      clearTimeout(renderModal);
    };
  }, [showEligibilityWarningModal]);
};

type SessionTabProps = PropsWithChildren<{
  active: boolean;
}>;

function SessionTab({ active, children }: SessionTabProps) {
  return <div style={{ display: active ? 'block' : 'none' }}>{children}</div>;
}

/**
 * ClinicianSession Documentation.
 *
 * This is V2 of the ClinicianSession component
 * This component only accepts v2 appointments and will not render v1 appointments
 * @component
 * @Test_Driven_Development
 * Rendered Components Which Can be Given Stubbed Data
 * @component ClinicianSession.BACKGROUND
 * @component ClinicianSession.SECURITY
 * @component ClinicianSession.CLINICAL
 * @component ClinicianSession.ACTIONS
 * @component ClinicianSession.LETTER
 * @component ClinicianSession.EndAppointmentDropdown
 * @component ClinicianSession.PatientStatusPanel
 * @component ClinicianSession.PatientInfoPanel
 * @component ClinicianSession.useDPACheck
 *
 * @returns {React.FC}
 */
const ClinicianSession = () => {
  const { t } = useTranslation('common');
  const appt_id = useSessionAppointmentId();
  const { appointment } = useSessionAppointment();
  const { detailsCurrentAppointment } = useAppointmentDetails(appt_id);
  const [isDPACheck] = useSessionFormState('isDPACheck');
  const { isSessionSavePending } = useIsSessionSaving();
  const { isLoading: isElligibilityLoading } = useEligibilityChecks();
  const [activeTab, setActiveTab] = useState<ActiveTabType>(
    () => (sessionStorage.getItem(`${appt_id}-activeTab`) as ActiveTabType) || StepTabs.BACKGROUND
  );

  const contentRef = useRef<HTMLDivElement>(null);

  const { data: appointmentMeta, isLoading: isAppointmentMetaLoading } = useAppointmentMeta();
  const { debouncedMutate: debouncedSaveActions } = useSessionOutcomeMutation();
  useAppointmentGrabbed(appointment);
  useCallStatusUpdaters(appointment);
  useMyStatusUpdater(appointment, appointment.read_only);

  const showSecurityTab =
    isDPACheck ||
    String(detailsCurrentAppointment?.audio_call_occurred) === 'true' ||
    String(detailsCurrentAppointment?.video_call_occurred) === 'true';

  const isSessionLoading = isAppointmentMetaLoading || isElligibilityLoading; // Main loading state for the session

  const isCaseReview = appointment.case_review;

  useVirtualOnlyWarning({ isSessionLoading, activeTab });

  const onChangeActiveTab = (index: number) => {
    if (index < 0 || index >= activeTabs.length - (appointment.appointment_completed ? 0 : 1)) return;
    // Save Actions on tab change
    if (activeTab === StepTabs.ACTIONS) debouncedSaveActions({});
    const activeTabId = activeTabs[index].id;
    setActiveTab(activeTabId);
    sessionStorage.setItem(`${appt_id}-activeTab`, activeTabId);
  };

  const StepTitle = (title: string) => (
    <span className="flow-button" data-testid={`step-tab-btn-${title.toLowerCase().replace(/\s/g, '')}`}>
      {title}
    </span>
  );
  const sessionSteps: ActiveTabProps[] = [
    {
      title: StepTitle('Background'),
      id: StepTabs.BACKGROUND,
      stepComponent: ClinicianSession.BACKGROUND
    },
    { title: StepTitle('Data Security'), id: StepTabs.SECURITY, stepComponent: ClinicianSession.SECURITY },
    { title: StepTitle('Clinical'), id: StepTabs.CLINICAL, stepComponent: ClinicianSession.CLINICAL },
    { title: StepTitle('Actions'), id: StepTabs.ACTIONS, stepComponent: ClinicianSession.ACTIONS },
    { title: StepTitle('Letters'), id: StepTabs.LETTER, stepComponent: () => null },
    {
      title: <ClinicianSession.EndAppointmentDropdown />,
      id: StepTabs.END,
      stepComponent: () => null // No tab content will ever be rendered for a dropdown tab
    }
  ];

  const activeTabsFilter = (tab: ActiveTabProps) => {
    if (tab.id === StepTabs.SECURITY && !showSecurityTab) return false;
    if (tab.id === StepTabs.CLINICAL && !appointmentMeta?.notes_tab) return false;
    if (tab.id === StepTabs.END && appointment.appointment_completed) return false;
    return true;
  };

  const activeTabs: ActiveTabProps[] = sessionSteps.filter(activeTabsFilter);
  const activeTabIndex = activeTabs.findIndex(({ id }) => id === activeTab);
  const stepItems: StepProps[] = activeTabs.map((tab, index) => ({
    icon: <></>,
    status: 'process',
    disabled: tab.id === StepTabs.END, // If the tab is a dropdown, disable it
    title: tab.title
  }));

  return (
    <HeidiErrorProvider>
      <HeidiSessionProvider>
        <ClinicianSessionStyled>
          <ClinicianSession.PatientStatusPanel
            onExpandedChanged={(expanded: boolean) => {
              if (expanded) {
                contentRef?.current?.classList.add('call-window-expanded');
              } else {
                contentRef?.current?.classList.remove('call-window-expanded');
              }
            }}
          />
          <div
            ref={contentRef}
            className={`session-view ${appointment.appointment_completed ? 'session-view__completed' : ''}`}
          >
            <AppointmentAlert />
            {!isCaseReview && <ClinicianMediaPermissionsWarning />}
            <ClinicianSession.PatientInfoPanel />
            <StyledFlow>
              <div className="step-container">
                <Button
                  icon={<LeftOutlined />}
                  size="large"
                  type="link"
                  data-testid="previous-step"
                  onClick={() => onChangeActiveTab(activeTabIndex - 1)}
                />
                <Steps
                  responsive
                  size="small"
                  type="navigation"
                  labelPlacement="vertical"
                  current={activeTabIndex}
                  onChange={onChangeActiveTab}
                  items={stepItems}
                />
                <Button
                  icon={<RightOutlined />}
                  size="large"
                  type="link"
                  data-testid="next-step"
                  onClick={() => onChangeActiveTab(activeTabIndex + 1)}
                />
              </div>
            </StyledFlow>

            <div className="session-steps-content" style={{ position: 'relative' }}>
              <Suspense fallback={<SessionLoader message={t('Initialising appointment form...')} />}>
                <Spin
                  spinning={isSessionSavePending}
                  size="small"
                  tip="Fetching letter..."
                  style={{
                    position: 'absolute',
                    top: '8px',
                    left: '98%',
                    zIndex: 1000
                  }}
                />
                {activeTabs.map((tab) => {
                  const TabComponent = tab.stepComponent;

                  return (
                    <SessionTab active={activeTab === tab.id} key={tab.id}>
                      <Suspense fallback={<SessionLoader message={t('Initialising appointment form...')} />}>
                        <TabComponent />
                      </Suspense>
                    </SessionTab>
                  );
                })}
                {activeTab === StepTabs.LETTER && <ClinicianSession.LETTER />}
              </Suspense>
            </div>
          </div>
        </ClinicianSessionStyled>
      </HeidiSessionProvider>
    </HeidiErrorProvider>
  );
};
const Pad = styled.div`
  padding: 1em 1em 1em 1em;
`;

const PaddedStep = (Component: React.FC<any>) => {
  return (props: any) => (
    <Pad>
      <Component {...props} />
    </Pad>
  );
};

ClinicianSession.BACKGROUND = Background as React.FC;
ClinicianSession.SECURITY = DataProtection;
ClinicianSession.CLINICAL = Clinical;
ClinicianSession.ACTIONS = PaddedStep(Actions);
ClinicianSession.LETTER = PaddedStep(LetterGeneration);
ClinicianSession.EndAppointmentDropdown = EndAppointmentDropdown;
ClinicianSession.PatientStatusPanel = PatientStatusPanel;
ClinicianSession.PatientInfoPanel = PatientInformation;

export default ClinicianSession;
